import { Component, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Progress Ring Component
 * 
 * Circular progress indicator for displaying completion percentages.
 * Perfect for project/program progress visualization.
 * 
 * @example
 * ```html
 * <mg3p-progress-ring
 *   [value]="75"
 *   [max]="100"
 *   size="lg"
 *   color="primary"
 *   [showLabel]="true"
 * />
 * ```
 * 
 * Features:
 * - Circular progress visualization
 * - Multiple sizes (sm, md, lg, xl)
 * - Color variants
 * - Percentage label
 * - Animated transitions
 * - Accessible (ARIA)
 */
@Component({
  selector: 'mg3p-progress-ring',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div 
      class="progress-ring"
      [class]="'progress-ring--' + size()"
      role="progressbar"
      [attr.aria-valuenow]="value()"
      [attr.aria-valuemin]="0"
      [attr.aria-valuemax]="max()"
      [attr.aria-label]="label() || percentage() + '% completo'"
    >
      <svg class="progress-ring__svg" [attr.viewBox]="viewBox()">
        <!-- Background circle -->
        <circle
          class="progress-ring__circle progress-ring__circle--bg"
          [attr.cx]="center()"
          [attr.cy]="center()"
          [attr.r]="radius()"
          [attr.stroke-width]="strokeWidth()"
        />
        
        <!-- Progress circle -->
        <circle
          class="progress-ring__circle progress-ring__circle--progress"
          [class]="'progress-ring__circle--' + color()"
          [attr.cx]="center()"
          [attr.cy]="center()"
          [attr.r]="radius()"
          [attr.stroke-width]="strokeWidth()"
          [attr.stroke-dasharray]="circumference()"
          [attr.stroke-dashoffset]="dashOffset()"
        />
      </svg>

      @if (showLabel()) {
        <div class="progress-ring__label">
          <div class="progress-ring__value">{{ percentage() }}%</div>
          @if (label()) {
            <div class="progress-ring__text">{{ label() }}</div>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    .progress-ring {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .progress-ring--sm {
      width: 60px;
      height: 60px;
    }

    .progress-ring--md {
      width: 100px;
      height: 100px;
    }

    .progress-ring--lg {
      width: 140px;
      height: 140px;
    }

    .progress-ring--xl {
      width: 180px;
      height: 180px;
    }

    .progress-ring__svg {
      width: 100%;
      height: 100%;
      transform: rotate(-90deg);
    }

    .progress-ring__circle {
      fill: none;
      transition: stroke-dashoffset var(--transition-normal);
    }

    .progress-ring__circle--bg {
      stroke: var(--color-surface-secondary);
    }

    .progress-ring__circle--progress {
      stroke-linecap: round;
    }

    .progress-ring__circle--primary {
      stroke: var(--color-primary);
    }

    .progress-ring__circle--success {
      stroke: var(--color-success);
    }

    .progress-ring__circle--warning {
      stroke: var(--color-warning);
    }

    .progress-ring__circle--danger {
      stroke: var(--color-danger);
    }

    .progress-ring__label {
      position: absolute;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: var(--spacing-sm);
    }

    .progress-ring__value {
      font-size: var(--font-size-xl);
      font-weight: var(--font-weight-bold);
      color: var(--color-text-primary);
      line-height: 1;
    }

    .progress-ring--sm .progress-ring__value {
      font-size: var(--font-size-md);
    }

    .progress-ring--xl .progress-ring__value {
      font-size: var(--font-size-3xl);
    }

    .progress-ring__text {
      font-size: var(--font-size-xs);
      color: var(--color-text-secondary);
      margin-top: var(--spacing-xs);
    }

    .progress-ring--sm .progress-ring__text {
      font-size: 10px;
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .progress-ring__circle {
        transition: none;
      }
    }
  `]
})
export class ProgressRingComponent {
  // Inputs
  value = input.required<number>();
  max = input<number>(100);
  size = input<'sm' | 'md' | 'lg' | 'xl'>('md');
  color = input<'primary' | 'success' | 'warning' | 'danger'>('primary');
  showLabel = input<boolean>(true);
  label = input<string>('');

  // Computed
  percentage = computed(() => {
    return Math.round((this.value() / this.max()) * 100);
  });

  sizeValue = computed(() => {
    const sizes = { sm: 60, md: 100, lg: 140, xl: 180 };
    return sizes[this.size()];
  });

  center = computed(() => this.sizeValue() / 2);
  
  strokeWidth = computed(() => {
    const widths = { sm: 4, md: 6, lg: 8, xl: 10 };
    return widths[this.size()];
  });

  radius = computed(() => {
    return (this.sizeValue() - this.strokeWidth()) / 2;
  });

  circumference = computed(() => {
    return 2 * Math.PI * this.radius();
  });

  dashOffset = computed(() => {
    const progress = this.value() / this.max();
    return this.circumference() * (1 - progress);
  });

  viewBox = computed(() => {
    const size = this.sizeValue();
    return `0 0 ${size} ${size}`;
  });
}

// Made with Bob
